import React, { useState, useEffect, useRef } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Textarea } from './ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui/select';
import { Badge } from './ui/badge';
import { Checkbox } from './ui/checkbox';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from './ui/dialog';
import {
  Plus, Search, MapPin, Briefcase, Calendar, Building2, Users,
  Car, Bus, Heart, Pencil, Trash2, Loader2, Filter, ChevronDown,
  FileText, Send, CheckCircle, Clock, XCircle, AlertCircle, Upload, File,
  ExternalLink, Globe, RefreshCw
} from 'lucide-react';
import { toast } from 'sonner';
import api from '../services/api';
import { useAuth } from '../context/AuthContext';

const STATUT_LABELS = {
  'à_l_etude': { label: 'À l\'étude', color: 'bg-blue-100 text-blue-700', icon: Clock },
  'entretien_prévu': { label: 'Entretien prévu', color: 'bg-amber-100 text-amber-700', icon: Calendar },
  'embauché': { label: 'Embauché(e)', color: 'bg-emerald-100 text-emerald-700', icon: CheckCircle },
  'non_retenu': { label: 'Non retenu(e)', color: 'bg-red-100 text-red-700', icon: XCircle }
};

const MOBILITE_ICONS = {
  'Accessible en transport': Bus,
  'Véhicule indispensable': Car,
  'Covoiturage possible': Users
};

const BourseEmploi = ({ canCreate = false, canEdit = false, canDelete = false, isBeneficiaire = false }) => {
  const { user } = useAuth();
  const [offres, setOffres] = useState([]);
  const [options, setOptions] = useState(null);
  const [referents, setReferents] = useState([]);
  const [entreprises, setEntreprises] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showAddDialog, setShowAddDialog] = useState(false);
  const [editingOffre, setEditingOffre] = useState(null);
  const [selectedOffre, setSelectedOffre] = useState(null);
  const [showDetailDialog, setShowDetailDialog] = useState(false);
  const [showCandidatureDialog, setShowCandidatureDialog] = useState(false);
  const [candidatureOffre, setCandidatureOffre] = useState(null);
  const [cvFile, setCvFile] = useState(null);
  const [candidatureMessage, setCandidatureMessage] = useState('');
  const [submittingCandidature, setSubmittingCandidature] = useState(false);
  const [syncingFranceTravail, setSyncingFranceTravail] = useState(false);
  const [lastRefresh, setLastRefresh] = useState(new Date());
  const cvInputRef = useRef(null);
  
  // Filtres : code_postal + multi-communes (strict, server-side) + secteur + type
  const [filters, setFilters] = useState({
    code_postal: '',
    communes: [],
    secteur: '',
    type_contrat: ''
  });
  const [showFilters, setShowFilters] = useState(false);
  const [communesList, setCommunesList] = useState([]);
  
  // Formulaire
  const [formData, setFormData] = useState({
    type_contrat: '',
    secteur_activite: '',
    intitule_poste: '',
    lieu_mission: '',
    zone_geographique: '',
    description_missions: '',
    prerequis: '',
    date_debut: '',
    niveau_mobilite: '',
    eligible_aides: false,
    referent_sof_id: '',
    entreprise_id: '',
    // Champs entreprise (pour création automatique)
    entreprise_nom: '',
    entreprise_siret: '',
    entreprise_adresse: '',
    entreprise_telephone: '',
    entreprise_email: '',
    entreprise_contact_nom: '',
    entreprise_contact_fonction: ''
  });
  
  const [showNewEntreprise, setShowNewEntreprise] = useState(false);

  useEffect(() => {
    // Chargement initial + rechargement uniquement quand les filtres changent.
    // Pas de polling : le rafraichissement se fait via le bouton manuel ou apres une action.
    loadData();
  }, [filters]);

  const loadData = async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      if (filters.code_postal) params.append('code_postal', filters.code_postal);
      if (filters.communes && filters.communes.length > 0) {
        params.append('communes', filters.communes.join('|'));
      }
      if (filters.secteur) params.append('secteur', filters.secteur);
      if (filters.type_contrat) params.append('type_contrat', filters.type_contrat);

      const [offresRes, optionsRes] = await Promise.all([
        api.get(`/offres-emploi?${params.toString()}`),
        api.get('/bourse-emploi/options')
      ]);

      setOffres(offresRes.data);
      setOptions(optionsRes.data);
      setLastRefresh(new Date());

      // Charger la liste des communes pour le filtre (une seule fois)
      if (communesList.length === 0) {
        try {
          const communesRes = await api.get('/bourse-emploi/communes-filtre');
          setCommunesList(communesRes.data?.communes || []);
        } catch (_) {
          // silencieux : filtre commune restera vide si endpoint indisponible
        }
      }

      // Charger les référents et entreprises si on peut créer
      if (canCreate) {
        const [referentsRes, entreprisesRes] = await Promise.all([
          api.get('/bourse-emploi/referents'),
          api.get('/entreprises').catch(() => ({ data: [] }))
        ]);
        setReferents(referentsRes.data);
        setEntreprises(entreprisesRes.data);
      }
    } catch (error) {
      toast.error('Erreur de chargement des offres');
    } finally {
      setLoading(false);
    }
  };

  const toggleCommune = (nomCommune) => {
    setFilters(prev => {
      const exists = prev.communes.includes(nomCommune);
      return {
        ...prev,
        communes: exists
          ? prev.communes.filter(c => c !== nomCommune)
          : [...prev.communes, nomCommune]
      };
    });
  };

  const handleSyncFranceTravail = async () => {
    if (syncingFranceTravail) return;
    setSyncingFranceTravail(true);
    try {
      const response = await api.post('/offres-france-travail/sync');
      toast.success(response.data?.message || 'Synchronisation lancée');
      // Refresh progressif : 2s (premiere remontee), 6s (fin sync), 12s (filet de securite)
      setTimeout(() => loadData(), 2000);
      setTimeout(() => loadData(), 6000);
      setTimeout(() => { loadData(); setSyncingFranceTravail(false); }, 12000);
    } catch (error) {
      const msg = error.response?.data?.detail || 'Erreur lors de la synchronisation';
      toast.error(msg);
      setSyncingFranceTravail(false);
    }
  };

  const resetForm = () => {
    setFormData({
      type_contrat: '',
      secteur_activite: '',
      intitule_poste: '',
      lieu_mission: '',
      zone_geographique: '',
      description_missions: '',
      prerequis: '',
      date_debut: '',
      niveau_mobilite: '',
      eligible_aides: false,
      referent_sof_id: '',
      entreprise_id: '',
      entreprise_nom: '',
      entreprise_siret: '',
      entreprise_adresse: '',
      entreprise_telephone: '',
      entreprise_email: '',
      entreprise_contact_nom: '',
      entreprise_contact_fonction: ''
    });
    setEditingOffre(null);
    setShowNewEntreprise(false);
  };

  const handleSubmit = async () => {
    if (!formData.intitule_poste || !formData.type_contrat || !formData.zone_geographique) {
      toast.error('Veuillez remplir les champs obligatoires');
      return;
    }

    // Si nouvelle entreprise, vérifier le nom
    if (showNewEntreprise && !formData.entreprise_nom) {
      toast.error('Veuillez saisir le nom de l\'entreprise');
      return;
    }

    try {
      // Mapper les champs du frontend vers le backend
      const payload = {
        intitule_poste: formData.intitule_poste,
        type_contrat: formData.type_contrat,
        secteur_activite: formData.secteur_activite,
        lieu_mission: formData.lieu_mission,
        zone_geographique: formData.zone_geographique,
        description: formData.description_missions,
        competences_requises: formData.prerequis,
        date_debut_souhaitee: formData.date_debut,
        referent_sof_id: formData.referent_sof_id,
        entreprise_id: formData.entreprise_id,
        entreprise_nom: formData.entreprise_nom,
        entreprise_siret: formData.entreprise_siret,
        entreprise_adresse: formData.entreprise_adresse,
        entreprise_telephone: formData.entreprise_telephone,
        entreprise_email: formData.entreprise_email,
        entreprise_contact_nom: formData.entreprise_contact_nom,
        entreprise_contact_fonction: formData.entreprise_contact_fonction,
        accessibilite_transport: formData.niveau_mobilite === 'Accessible en transport',
        vehicule_requis: formData.niveau_mobilite === 'Véhicule indispensable'
      };

      if (editingOffre) {
        await api.put(`/offres-emploi/${editingOffre.id}`, payload);
        toast.success('Offre mise à jour');
      } else {
        const response = await api.post('/offres-emploi', payload);
        // Afficher le message retourné par l'API (différent selon le rôle)
        const message = response.data?.message || 'Offre créée avec succès';
        toast.success(message + (showNewEntreprise ? ' (fiche entreprise générée)' : ''));
      }

      setShowAddDialog(false);
      resetForm();
      loadData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erreur lors de l\'enregistrement');
    }
  };

  const handleEdit = (offre) => {
    setFormData({
      type_contrat: offre.type_contrat || '',
      secteur_activite: offre.secteur_activite || '',
      intitule_poste: offre.intitule_poste || '',
      lieu_mission: offre.lieu_mission || '',
      zone_geographique: offre.zone_geographique || '',
      description_missions: offre.description_missions || '',
      prerequis: offre.prerequis || '',
      date_debut: offre.date_debut || '',
      niveau_mobilite: offre.niveau_mobilite || '',
      eligible_aides: offre.eligible_aides || false,
      referent_sof_id: offre.referent_sof_id || '',
      entreprise_id: offre.entreprise_id || ''
    });
    setEditingOffre(offre);
    setShowAddDialog(true);
  };

  const handleDelete = async (offreId) => {
    if (!window.confirm('Supprimer cette offre ?')) return;
    
    try {
      await api.delete(`/offres-emploi/${offreId}`);
      toast.success('Offre supprimée');
      loadData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erreur de suppression');
    }
  };

  const handlePostuler = async (offre) => {
    // Ouvrir le dialogue de candidature avec upload CV
    setCandidatureOffre(offre);
    setCvFile(null);
    setCandidatureMessage('');
    setShowCandidatureDialog(true);
    setShowDetailDialog(false);
  };

  const submitCandidature = async () => {
    if (!candidatureOffre) return;
    
    setSubmittingCandidature(true);
    try {
      const formData = new FormData();
      formData.append('offre_id', candidatureOffre.id);
      if (candidatureMessage) {
        formData.append('message', candidatureMessage);
      }
      if (cvFile) {
        formData.append('cv_file', cvFile);
      }
      
      await api.post('/candidatures', formData, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });
      
      toast.success('Votre candidature a été envoyée ! ' + (cvFile ? 'CV joint.' : '') + ' Les intervenants ont été notifiés.');
      setShowCandidatureDialog(false);
      setCandidatureOffre(null);
      setCvFile(null);
      setCandidatureMessage('');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erreur lors de la candidature');
    } finally {
      setSubmittingCandidature(false);
    }
  };

  const handleCvChange = (e) => {
    const file = e.target.files?.[0];
    if (file) {
      // Vérifier le type de fichier
      const allowedTypes = ['application/pdf', 'application/msword', 'application/vnd.openxmlformats-officedocument.wordprocessingml.document'];
      if (!allowedTypes.includes(file.type)) {
        toast.error('Format de fichier non supporté. Utilisez PDF ou Word.');
        return;
      }
      // Vérifier la taille (5 MB max)
      if (file.size > 5 * 1024 * 1024) {
        toast.error('Le fichier est trop volumineux (max 5 MB)');
        return;
      }
      setCvFile(file);
    }
  };

  const getMobiliteIcon = (niveau) => {
    const Icon = MOBILITE_ICONS[niveau] || Car;
    return Icon;
  };

  const activeFiltersCount = (
    (filters.code_postal ? 1 : 0) +
    (filters.communes && filters.communes.length > 0 ? 1 : 0) +
    (filters.secteur ? 1 : 0) +
    (filters.type_contrat ? 1 : 0)
  );

  return (
    <div className="space-y-6">
      {/* En-tête */}
      <Card className="bg-gradient-to-r from-blue-50 to-indigo-50 border-blue-200 rounded-3xl overflow-hidden">
        <CardHeader className="pb-2">
          <div className="flex items-center justify-between flex-wrap gap-4">
            <div>
              <CardTitle className="text-2xl text-blue-900 flex items-center gap-2" style={{ fontFamily: 'Nunito, sans-serif' }}>
                <Briefcase className="w-7 h-7" />
                Bourse à l'Emploi / Immersion
              </CardTitle>
              <p className="text-blue-700 mt-1 flex items-center gap-2 flex-wrap">
                <span className="font-semibold">{offres.length} offre(s) disponible(s)</span>
                {loading ? (
                  <span className="inline-flex items-center text-xs text-blue-500">
                    <Loader2 className="w-3 h-3 mr-1 animate-spin" />
                    actualisation…
                  </span>
                ) : (
                  <>
                    <span className="text-xs text-blue-400">
                      · Mise à jour {(() => {
                        const sec = Math.round((new Date() - lastRefresh) / 1000);
                        if (sec < 5) return "à l'instant";
                        if (sec < 60) return `il y a ${sec}s`;
                        const min = Math.floor(sec / 60);
                        return `il y a ${min} min`;
                      })()}
                    </span>
                    <button
                      type="button"
                      onClick={() => loadData()}
                      className="inline-flex items-center text-xs text-blue-600 hover:text-blue-800 hover:underline"
                      title="Rafraichir la liste"
                    >
                      <RefreshCw className="w-3 h-3 mr-1" />
                      Rafraîchir
                    </button>
                  </>
                )}
              </p>
            </div>
            <div className="flex items-center gap-3">
              <Button 
                variant="outline"
                onClick={() => setShowFilters(!showFilters)}
                className={`rounded-xl ${activeFiltersCount > 0 ? 'border-blue-500 text-blue-700' : ''}`}
              >
                <Filter className="w-4 h-4 mr-2" />
                Filtres
                {activeFiltersCount > 0 && (
                  <Badge className="ml-2 bg-blue-500 text-white">{activeFiltersCount}</Badge>
                )}
              </Button>
              {user?.role === 'admin' && (
                <Button
                  onClick={handleSyncFranceTravail}
                  disabled={syncingFranceTravail}
                  variant="outline"
                  className="rounded-xl border-indigo-300 text-indigo-700 hover:bg-indigo-50"
                  title="Importer les nouvelles offres depuis France Travail"
                >
                  {syncingFranceTravail ? (
                    <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                  ) : (
                    <RefreshCw className="w-4 h-4 mr-2" />
                  )}
                  {syncingFranceTravail ? 'Synchronisation…' : 'Synchroniser France Travail'}
                </Button>
              )}
              {canCreate && (
                <Button 
                  onClick={() => { resetForm(); setShowAddDialog(true); }}
                  className="bg-blue-600 hover:bg-blue-700 rounded-xl shadow-lg"
                >
                  <Plus className="w-4 h-4 mr-2" />
                  Nouvelle offre
                </Button>
              )}
            </div>
          </div>
        </CardHeader>
        
        {/* Filtres */}
        {showFilters && options && (
          <CardContent className="pt-4 border-t border-blue-200 mt-4">
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <div>
                <Label className="text-blue-800">Code postal</Label>
                <Input
                  type="text"
                  inputMode="numeric"
                  maxLength={5}
                  placeholder="Ex : 97215"
                  value={filters.code_postal}
                  onChange={(e) => {
                    // Garder uniquement les chiffres (max 5)
                    const v = e.target.value.replace(/\D/g, '').slice(0, 5);
                    setFilters({ ...filters, code_postal: v });
                  }}
                  className="rounded-xl bg-white"
                />
              </div>
              <div>
                <Label className="text-blue-800">Secteur d'activité</Label>
                <Select value={filters.secteur} onValueChange={(v) => setFilters({...filters, secteur: v === 'all' ? '' : v})}>
                  <SelectTrigger className="rounded-xl bg-white">
                    <SelectValue placeholder="Tous les secteurs" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">Tous les secteurs</SelectItem>
                    {options.secteurs_activite.map(s => (
                      <SelectItem key={s} value={s}>{s}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label className="text-blue-800">Type de contrat</Label>
                <Select value={filters.type_contrat} onValueChange={(v) => setFilters({...filters, type_contrat: v === 'all' ? '' : v})}>
                  <SelectTrigger className="rounded-xl bg-white">
                    <SelectValue placeholder="Tous les types" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">Tous les types</SelectItem>
                    {options.types_contrat.map(t => (
                      <SelectItem key={t} value={t}>{t}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>
            {/* Sélecteur multi-communes (filtre strict côté serveur) */}
            {communesList.length > 0 && (
              <div className="mt-4">
                <Label className="text-blue-800 flex items-center gap-2">
                  <MapPin className="w-4 h-4" />
                  Communes ({filters.communes.length} sélectionnée{filters.communes.length > 1 ? 's' : ''})
                </Label>
                <p className="text-xs text-blue-600 mt-1 mb-2">
                  Filtre strict par nom de commune (évite les offres mal localisées par France Travail)
                </p>
                <div className="flex flex-wrap gap-2 mt-2 p-3 bg-white border border-blue-200 rounded-xl max-h-48 overflow-y-auto">
                  {communesList.map((c) => {
                    const isSelected = filters.communes.includes(c.nom);
                    return (
                      <button
                        key={c.code_insee}
                        type="button"
                        onClick={() => toggleCommune(c.nom)}
                        className={`px-3 py-1.5 rounded-full text-xs font-medium transition-colors border ${
                          isSelected
                            ? 'bg-blue-600 text-white border-blue-700 shadow-sm'
                            : 'bg-white text-slate-700 border-slate-200 hover:border-blue-400 hover:bg-blue-50'
                        }`}
                      >
                        {c.nom}
                      </button>
                    );
                  })}
                </div>
                {filters.communes.length > 0 && (
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => setFilters(prev => ({ ...prev, communes: [] }))}
                    className="mt-2 text-blue-600"
                  >
                    Désélectionner toutes les communes
                  </Button>
                )}
              </div>
            )}

            {activeFiltersCount > 0 && (
              <Button
                variant="ghost"
                size="sm"
                onClick={() => setFilters({ code_postal: '', communes: [], secteur: '', type_contrat: '' })}
                className="mt-3 text-blue-600"
              >
                Réinitialiser tous les filtres
              </Button>
            )}
          </CardContent>
        )}
      </Card>

      {/* Liste des offres */}
      {loading ? (
        <div className="flex items-center justify-center py-12">
          <Loader2 className="w-8 h-8 animate-spin text-blue-600" />
        </div>
      ) : offres.length === 0 ? (
        <Card className="bg-white border-slate-200 rounded-3xl p-8 text-center">
          <Briefcase className="w-12 h-12 text-slate-300 mx-auto mb-4" />
          <p className="text-slate-500 font-medium">Aucune offre disponible</p>
          <p className="text-sm text-slate-400 mt-1">
            {activeFiltersCount > 0 ? 'Essayez de modifier vos filtres' : 'Les nouvelles offres apparaîtront ici'}
          </p>
        </Card>
      ) : (
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
          {offres.map((offre) => {
            const MobiliteIcon = getMobiliteIcon(offre.niveau_mobilite);
            
            return (
              <Card 
                key={offre.id} 
                className="bg-white border-slate-200 rounded-3xl shadow-sm hover:shadow-md transition-shadow cursor-pointer"
                onClick={() => { setSelectedOffre(offre); setShowDetailDialog(true); }}
              >
                <CardContent className="p-5">
                  <div className="flex items-start justify-between gap-4">
                    <div className="flex-1">
                      {/* Type de contrat */}
                      <div className="flex flex-wrap items-center gap-2 mb-2">
                        <Badge className={`${
                          offre.type_contrat === 'CDI' ? 'bg-emerald-100 text-emerald-700' :
                          offre.type_contrat === 'CDD' ? 'bg-blue-100 text-blue-700' :
                          offre.type_contrat === 'Immersion (PMSMP)' ? 'bg-purple-100 text-purple-700' :
                          offre.type_contrat === 'Alternance' ? 'bg-amber-100 text-amber-700' :
                          'bg-slate-100 text-slate-700'
                        }`}>
                          {offre.type_contrat}
                        </Badge>
                        <Badge variant="outline" className="text-slate-600">
                          {offre.secteur_activite}
                        </Badge>
                        {offre.source === 'france_travail' && (
                          <Badge className="bg-indigo-100 text-indigo-700 border-indigo-200">
                            <Globe className="w-3 h-3 mr-1" />
                            France Travail
                          </Badge>
                        )}
                        {offre.eligible_aides && (
                          <Badge className="bg-pink-100 text-pink-700">
                            <Heart className="w-3 h-3 mr-1" />
                            Aides
                          </Badge>
                        )}
                      </div>
                      
                      {/* Titre */}
                      <h3 className="font-bold text-lg text-slate-900 mb-1">{offre.intitule_poste}</h3>
                      
                      {/* Entreprise */}
                      {offre.entreprise_nom && (
                        <p className="text-slate-600 flex items-center gap-1 mb-2">
                          <Building2 className="w-4 h-4" />
                          {offre.entreprise_nom}
                        </p>
                      )}
                      
                      {/* Infos */}
                      <div className="flex flex-wrap items-center gap-3 text-sm text-slate-500">
                        <span className="flex items-center gap-1">
                          <MapPin className="w-4 h-4" />
                          {offre.lieu_mission}{offre.zone_geographique && offre.source !== 'france_travail' ? ` (${offre.zone_geographique})` : ''}
                        </span>
                        {(offre.date_debut || offre.date_debut_souhaitee) && (
                          <span className="flex items-center gap-1">
                            <Calendar className="w-4 h-4" />
                            {new Date(offre.date_debut || offre.date_debut_souhaitee).toLocaleDateString('fr-FR')}
                          </span>
                        )}
                        {offre.niveau_mobilite && (
                          <span className="flex items-center gap-1">
                            <MobiliteIcon className="w-4 h-4" />
                            {offre.niveau_mobilite}
                          </span>
                        )}
                      </div>
                    </div>
                    
                    {/* Actions — masquees pour les offres France Travail */}
                    {(canEdit || canDelete) && offre.source !== 'france_travail' && (
                      <div className="flex gap-1" onClick={(e) => e.stopPropagation()}>
                        {canEdit && (
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => handleEdit(offre)}
                            className="text-slate-500 hover:text-blue-600"
                          >
                            <Pencil className="w-4 h-4" />
                          </Button>
                        )}
                        {canDelete && (
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => handleDelete(offre.id)}
                            className="text-slate-500 hover:text-red-600"
                          >
                            <Trash2 className="w-4 h-4" />
                          </Button>
                        )}
                      </div>
                    )}
                  </div>

                  {/* Bouton "Je veux me positionner" — visible directement sur la carte pour les bénéficiaires */}
                  {isBeneficiaire && (
                    <div className="mt-4 pt-4 border-t border-slate-100 flex items-center justify-between gap-2" onClick={(e) => e.stopPropagation()}>
                      <span className="text-xs text-slate-500">
                        {offre.source === 'france_travail'
                          ? 'Offre France Travail — votre référent sera notifié'
                          : 'Postuler à cette offre'}
                      </span>
                      <Button
                        size="sm"
                        onClick={() => handlePostuler(offre)}
                        className="rounded-xl bg-emerald-600 hover:bg-emerald-700 text-white"
                      >
                        <Send className="w-3.5 h-3.5 mr-1.5" />
                        Je veux me positionner
                      </Button>
                    </div>
                  )}
                </CardContent>
              </Card>
            );
          })}
        </div>
      )}

      {/* Dialog Détail Offre */}
      <Dialog open={showDetailDialog} onOpenChange={setShowDetailDialog}>
        <DialogContent className="sm:max-w-2xl rounded-3xl max-h-[90vh] overflow-y-auto">
          {selectedOffre && (
            <>
              <DialogHeader>
                <div className="flex flex-wrap items-center gap-2 mb-2">
                  <Badge className={`${
                    selectedOffre.type_contrat === 'CDI' ? 'bg-emerald-100 text-emerald-700' :
                    selectedOffre.type_contrat === 'CDD' ? 'bg-blue-100 text-blue-700' :
                    selectedOffre.type_contrat === 'Immersion (PMSMP)' ? 'bg-purple-100 text-purple-700' :
                    'bg-amber-100 text-amber-700'
                  }`}>
                    {selectedOffre.type_contrat}
                  </Badge>
                  {selectedOffre.source === 'france_travail' && (
                    <Badge className="bg-indigo-100 text-indigo-700 border-indigo-200">
                      <Globe className="w-3 h-3 mr-1" />
                      France Travail
                    </Badge>
                  )}
                  {selectedOffre.eligible_aides && (
                    <Badge className="bg-pink-100 text-pink-700">
                      <Heart className="w-3 h-3 mr-1" />
                      Éligible aux aides
                    </Badge>
                  )}
                </div>
                <DialogTitle className="text-2xl" style={{ fontFamily: 'Nunito, sans-serif' }}>
                  {selectedOffre.intitule_poste}
                </DialogTitle>
                <DialogDescription>
                  {selectedOffre.entreprise_nom && (
                    <span className="flex items-center gap-1 text-slate-600">
                      <Building2 className="w-4 h-4" />
                      {selectedOffre.entreprise_nom}
                    </span>
                  )}
                </DialogDescription>
              </DialogHeader>

              <div className="space-y-4 py-4">
                {/* Bandeau France Travail */}
                {selectedOffre.source === 'france_travail' && (
                  <div className="p-4 bg-indigo-50 border border-indigo-200 rounded-xl space-y-2">
                    <div className="flex items-center gap-2 text-indigo-700 font-semibold">
                      <Globe className="w-4 h-4" />
                      Offre publiée sur France Travail
                    </div>
                    <p className="text-sm text-indigo-900">
                      Vous pouvez postuler depuis Inserpro (votre référent suivra votre candidature),
                      mais pensez aussi à candidater directement sur France Travail pour maximiser vos chances.
                    </p>
                    {selectedOffre.offre_url_origine && (
                      <a
                        href={selectedOffre.offre_url_origine}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="inline-flex items-center gap-1 text-sm font-semibold text-indigo-700 hover:text-indigo-900 underline"
                      >
                        Voir l'annonce complète sur France Travail
                        <ExternalLink className="w-3.5 h-3.5" />
                      </a>
                    )}
                  </div>
                )}

                {/* Infos principales */}
                <div className="grid grid-cols-2 gap-4 p-4 bg-slate-50 rounded-xl">
                  <div>
                    <Label className="text-slate-500 text-sm">Lieu</Label>
                    <p className="font-medium flex items-center gap-1">
                      <MapPin className="w-4 h-4 text-blue-600" />
                      {selectedOffre.lieu_mission}{selectedOffre.zone_geographique && selectedOffre.source !== 'france_travail' ? ` (${selectedOffre.zone_geographique})` : ''}
                    </p>
                  </div>
                  {(selectedOffre.date_debut || selectedOffre.date_debut_souhaitee) && (
                    <div>
                      <Label className="text-slate-500 text-sm">Date de début</Label>
                      <p className="font-medium flex items-center gap-1">
                        <Calendar className="w-4 h-4 text-blue-600" />
                        {new Date(selectedOffre.date_debut || selectedOffre.date_debut_souhaitee).toLocaleDateString('fr-FR', {
                          weekday: 'long', day: 'numeric', month: 'long', year: 'numeric'
                        })}
                      </p>
                    </div>
                  )}
                  <div>
                    <Label className="text-slate-500 text-sm">Secteur</Label>
                    <p className="font-medium">{selectedOffre.secteur_activite}</p>
                  </div>
                  {selectedOffre.niveau_mobilite && (
                    <div>
                      <Label className="text-slate-500 text-sm">Mobilité</Label>
                      <p className="font-medium flex items-center gap-1">
                        {React.createElement(getMobiliteIcon(selectedOffre.niveau_mobilite), { className: "w-4 h-4 text-blue-600" })}
                        {selectedOffre.niveau_mobilite}
                      </p>
                    </div>
                  )}
                  {selectedOffre.salaire && (
                    <div>
                      <Label className="text-slate-500 text-sm">Salaire</Label>
                      <p className="font-medium">{selectedOffre.salaire}</p>
                    </div>
                  )}
                </div>

                {/* Description */}
                {(selectedOffre.description_missions || selectedOffre.description) && (
                  <div>
                    <Label className="text-slate-700 font-semibold">Description des missions</Label>
                    <div className="mt-2 p-4 bg-white border rounded-xl whitespace-pre-wrap">
                      {selectedOffre.description_missions || selectedOffre.description}
                    </div>
                  </div>
                )}

                {/* Pré-requis (offres internes uniquement) */}
                {selectedOffre.prerequis && (
                  <div>
                    <Label className="text-slate-700 font-semibold">Pré-requis</Label>
                    <div className="mt-2 p-4 bg-white border rounded-xl whitespace-pre-wrap">
                      {selectedOffre.prerequis}
                    </div>
                  </div>
                )}

                {/* Contact Référent (offres internes) */}
                {selectedOffre.referent_sof_nom && selectedOffre.source !== 'france_travail' && (
                  <div className="p-4 bg-blue-50 border border-blue-200 rounded-xl">
                    <Label className="text-blue-700 font-semibold">Contact Référent SOF</Label>
                    <p className="font-medium text-blue-900 mt-1">{selectedOffre.referent_sof_nom}</p>
                    <p className="text-sm text-blue-600">Pour toute question avant de postuler</p>
                  </div>
                )}
              </div>

              <DialogFooter>
                <Button variant="outline" onClick={() => setShowDetailDialog(false)} className="rounded-xl">
                  Fermer
                </Button>
                {isBeneficiaire && (
                  <Button 
                    onClick={() => handlePostuler(selectedOffre)}
                    className="rounded-xl bg-emerald-600 hover:bg-emerald-700"
                  >
                    <Send className="w-4 h-4 mr-2" />
                    Je suis intéressé(e)
                  </Button>
                )}
              </DialogFooter>
            </>
          )}
        </DialogContent>
      </Dialog>

      {/* Dialog Ajouter/Modifier Offre */}
      <Dialog open={showAddDialog} onOpenChange={setShowAddDialog}>
        <DialogContent className="sm:max-w-2xl rounded-3xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2" style={{ fontFamily: 'Nunito, sans-serif' }}>
              <Plus className="w-5 h-5 text-blue-600" />
              {editingOffre ? 'Modifier l\'offre' : 'Nouvelle offre d\'emploi'}
            </DialogTitle>
            <DialogDescription>
              Remplissez les informations de l'offre
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-4 py-4">
            {/* Section 1: Infos principales */}
            <div className="p-4 bg-slate-50 rounded-xl space-y-4">
              <h4 className="font-semibold text-slate-700">Informations de l'offre</h4>
              
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label>Type de contrat *</Label>
                  <Select value={formData.type_contrat} onValueChange={(v) => setFormData({...formData, type_contrat: v})}>
                    <SelectTrigger className="rounded-xl">
                      <SelectValue placeholder="Sélectionner" />
                    </SelectTrigger>
                    <SelectContent>
                      {options?.types_contrat.map(t => (
                        <SelectItem key={t} value={t}>{t}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-2">
                  <Label>Secteur d'activité *</Label>
                  <Select value={formData.secteur_activite} onValueChange={(v) => setFormData({...formData, secteur_activite: v})}>
                    <SelectTrigger className="rounded-xl">
                      <SelectValue placeholder="Sélectionner" />
                    </SelectTrigger>
                    <SelectContent>
                      {options?.secteurs_activite.map(s => (
                        <SelectItem key={s} value={s}>{s}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              </div>

              <div className="space-y-2">
                <Label>Intitulé du poste *</Label>
                <Input
                  value={formData.intitule_poste}
                  onChange={(e) => setFormData({...formData, intitule_poste: e.target.value})}
                  placeholder="Ex: Aide-maçon, Serveur, Agent d'entretien"
                  className="rounded-xl"
                />
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label>Lieu de la mission *</Label>
                  <Input
                    value={formData.lieu_mission}
                    onChange={(e) => setFormData({...formData, lieu_mission: e.target.value})}
                    placeholder="Commune (ex: Fort-de-France)"
                    className="rounded-xl"
                  />
                </div>
                <div className="space-y-2">
                  <Label>Zone géographique *</Label>
                  <Select value={formData.zone_geographique} onValueChange={(v) => setFormData({...formData, zone_geographique: v})}>
                    <SelectTrigger className="rounded-xl">
                      <SelectValue placeholder="Sélectionner" />
                    </SelectTrigger>
                    <SelectContent>
                      {options?.zones.map(z => (
                        <SelectItem key={z} value={z}>{z}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              </div>

              <div className="space-y-2">
                <Label>Description des missions</Label>
                <Textarea
                  value={formData.description_missions}
                  onChange={(e) => setFormData({...formData, description_missions: e.target.value})}
                  placeholder="Liste des tâches principales..."
                  className="rounded-xl resize-none"
                  rows={4}
                />
              </div>

              <div className="space-y-2">
                <Label>Pré-requis</Label>
                <Textarea
                  value={formData.prerequis}
                  onChange={(e) => setFormData({...formData, prerequis: e.target.value})}
                  placeholder="Diplômes, expérience ou 'Débutant accepté'"
                  className="rounded-xl resize-none"
                  rows={2}
                />
              </div>

              <div className="space-y-2">
                <Label>Date de début *</Label>
                <Input
                  type="date"
                  value={formData.date_debut}
                  onChange={(e) => setFormData({...formData, date_debut: e.target.value})}
                  className="rounded-xl"
                />
              </div>
            </div>

            {/* Section 2: Spécial Insertion */}
            <div className="p-4 bg-blue-50 rounded-xl space-y-4">
              <h4 className="font-semibold text-blue-700">Spécial Insertion (Lot 7)</h4>
              
              <div className="space-y-2">
                <Label>Niveau de mobilité requis</Label>
                <Select value={formData.niveau_mobilite} onValueChange={(v) => setFormData({...formData, niveau_mobilite: v})}>
                  <SelectTrigger className="rounded-xl bg-white">
                    <SelectValue placeholder="Sélectionner" />
                  </SelectTrigger>
                  <SelectContent>
                    {options?.niveaux_mobilite.map(n => (
                      <SelectItem key={n} value={n}>{n}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              <div className="flex items-center space-x-2">
                <Checkbox 
                  id="eligible_aides"
                  checked={formData.eligible_aides}
                  onCheckedChange={(checked) => setFormData({...formData, eligible_aides: checked})}
                />
                <Label htmlFor="eligible_aides" className="cursor-pointer">
                  Éligible aux aides (PEC, contrat de professionnalisation...)
                </Label>
              </div>

              <div className="space-y-2">
                <Label>Contact Référent SOF</Label>
                <Select value={formData.referent_sof_id || 'none'} onValueChange={(v) => setFormData({...formData, referent_sof_id: v === 'none' ? '' : v})}>
                  <SelectTrigger className="rounded-xl bg-white">
                    <SelectValue placeholder="Sélectionner un référent" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="none">Aucun</SelectItem>
                    {referents.map(r => (
                      <SelectItem key={r.id} value={r.id}>{r.prenom} {r.nom}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>

            {/* Section 3: Entreprise */}
            <div className="p-4 bg-emerald-50 rounded-xl space-y-4 border border-emerald-200">
              <div className="flex items-center justify-between">
                <h4 className="font-semibold text-emerald-700">
                  <Building2 className="w-4 h-4 inline mr-2" />
                  Informations Entreprise
                </h4>
                <div className="flex items-center gap-2">
                  <Button 
                    type="button"
                    variant={!showNewEntreprise ? "default" : "outline"}
                    size="sm"
                    onClick={() => setShowNewEntreprise(false)}
                    className="rounded-lg text-xs"
                  >
                    Existante
                  </Button>
                  <Button 
                    type="button"
                    variant={showNewEntreprise ? "default" : "outline"}
                    size="sm"
                    onClick={() => { setShowNewEntreprise(true); setFormData({...formData, entreprise_id: ''}); }}
                    className="rounded-lg text-xs"
                  >
                    Nouvelle
                  </Button>
                </div>
              </div>
              
              {!showNewEntreprise ? (
                <div className="space-y-2">
                  <Label>Lier à une entreprise existante</Label>
                  <Select value={formData.entreprise_id} onValueChange={(v) => setFormData({...formData, entreprise_id: v === 'none' ? '' : v})}>
                    <SelectTrigger className="rounded-xl bg-white">
                      <SelectValue placeholder="Sélectionner une entreprise" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="none">Aucune</SelectItem>
                      {entreprises.map(e => (
                        <SelectItem key={e.id} value={e.id}>{e.nom} - {e.zone}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              ) : (
                <div className="space-y-4 bg-white p-4 rounded-xl border border-emerald-200">
                  <p className="text-sm text-emerald-700 font-medium">
                    Une fiche entreprise sera automatiquement créée
                  </p>
                  
                  <div className="grid grid-cols-2 gap-4">
                    <div className="space-y-2">
                      <Label>Nom de l'entreprise *</Label>
                      <Input
                        value={formData.entreprise_nom}
                        onChange={(e) => setFormData({...formData, entreprise_nom: e.target.value})}
                        placeholder="Ex: Super U"
                        className="rounded-xl"
                      />
                    </div>
                    <div className="space-y-2">
                      <Label>SIRET</Label>
                      <Input
                        value={formData.entreprise_siret}
                        onChange={(e) => setFormData({...formData, entreprise_siret: e.target.value})}
                        placeholder="14 chiffres"
                        className="rounded-xl"
                      />
                    </div>
                  </div>

                  <div className="space-y-2">
                    <Label>Adresse</Label>
                    <Input
                      value={formData.entreprise_adresse}
                      onChange={(e) => setFormData({...formData, entreprise_adresse: e.target.value})}
                      placeholder="Adresse complète"
                      className="rounded-xl"
                    />
                  </div>

                  <div className="grid grid-cols-2 gap-4">
                    <div className="space-y-2">
                      <Label>Téléphone</Label>
                      <Input
                        value={formData.entreprise_telephone}
                        onChange={(e) => setFormData({...formData, entreprise_telephone: e.target.value})}
                        placeholder="0262 XX XX XX"
                        className="rounded-xl"
                      />
                    </div>
                    <div className="space-y-2">
                      <Label>Email</Label>
                      <Input
                        type="email"
                        value={formData.entreprise_email}
                        onChange={(e) => setFormData({...formData, entreprise_email: e.target.value})}
                        placeholder="contact@entreprise.re"
                        className="rounded-xl"
                      />
                    </div>
                  </div>

                  <div className="grid grid-cols-2 gap-4">
                    <div className="space-y-2">
                      <Label>Nom du contact</Label>
                      <Input
                        value={formData.entreprise_contact_nom}
                        onChange={(e) => setFormData({...formData, entreprise_contact_nom: e.target.value})}
                        placeholder="M. / Mme"
                        className="rounded-xl"
                      />
                    </div>
                    <div className="space-y-2">
                      <Label>Fonction du contact</Label>
                      <Input
                        value={formData.entreprise_contact_fonction}
                        onChange={(e) => setFormData({...formData, entreprise_contact_fonction: e.target.value})}
                        placeholder="Responsable RH"
                        className="rounded-xl"
                      />
                    </div>
                  </div>
                </div>
              )}
            </div>
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setShowAddDialog(false)} className="rounded-xl">
              Annuler
            </Button>
            <Button onClick={handleSubmit} className="rounded-xl bg-blue-600 hover:bg-blue-700">
              <CheckCircle className="w-4 h-4 mr-2" />
              {editingOffre ? 'Mettre à jour' : 'Créer l\'offre'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Dialogue de candidature avec CV */}
      <Dialog open={showCandidatureDialog} onOpenChange={setShowCandidatureDialog}>
        <DialogContent className="sm:max-w-[500px] rounded-3xl">
          <DialogHeader>
            <DialogTitle style={{ fontFamily: 'Nunito, sans-serif' }} className="flex items-center gap-2">
              <Send className="w-5 h-5 text-blue-600" />
              Postuler à cette offre
            </DialogTitle>
            <DialogDescription>
              {candidatureOffre && (
                <span className="font-medium text-slate-700">
                  {candidatureOffre.intitule_poste} - {candidatureOffre.entreprise_nom}
                </span>
              )}
            </DialogDescription>
          </DialogHeader>
          
          <div className="space-y-4 py-4">
            {/* Upload CV */}
            <div className="space-y-2">
              <Label className="flex items-center gap-2">
                <FileText className="w-4 h-4 text-blue-600" />
                Joindre votre CV (optionnel)
              </Label>
              <div 
                className={`border-2 border-dashed rounded-xl p-6 text-center transition-all cursor-pointer
                  ${cvFile ? 'border-emerald-400 bg-emerald-50' : 'border-slate-300 hover:border-blue-400 hover:bg-blue-50'}`}
                onClick={() => cvInputRef.current?.click()}
              >
                <input
                  ref={cvInputRef}
                  type="file"
                  accept=".pdf,.doc,.docx"
                  onChange={handleCvChange}
                  className="hidden"
                />
                {cvFile ? (
                  <div className="flex items-center justify-center gap-3">
                    <File className="w-8 h-8 text-emerald-600" />
                    <div className="text-left">
                      <p className="font-medium text-emerald-700">{cvFile.name}</p>
                      <p className="text-xs text-emerald-600">
                        {(cvFile.size / 1024).toFixed(1)} Ko
                      </p>
                    </div>
                    <Button
                      type="button"
                      variant="ghost"
                      size="sm"
                      onClick={(e) => {
                        e.stopPropagation();
                        setCvFile(null);
                      }}
                      className="text-red-500 hover:text-red-700"
                    >
                      <XCircle className="w-4 h-4" />
                    </Button>
                  </div>
                ) : (
                  <>
                    <Upload className="w-10 h-10 text-slate-400 mx-auto mb-2" />
                    <p className="text-sm text-slate-600">Cliquez pour sélectionner votre CV</p>
                    <p className="text-xs text-slate-400 mt-1">PDF ou Word (max 5 MB)</p>
                  </>
                )}
              </div>
            </div>
            
            {/* Message de motivation (optionnel) */}
            <div className="space-y-2">
              <Label className="flex items-center gap-2">
                <FileText className="w-4 h-4 text-slate-600" />
                Message de motivation (optionnel)
              </Label>
              <Textarea
                value={candidatureMessage}
                onChange={(e) => setCandidatureMessage(e.target.value)}
                placeholder="Présentez brièvement votre motivation pour ce poste..."
                className="rounded-xl min-h-[100px]"
                maxLength={500}
              />
              <p className="text-xs text-slate-400 text-right">{candidatureMessage.length}/500</p>
            </div>
          </div>
          
          <DialogFooter className="flex gap-2">
            <Button
              variant="outline"
              onClick={() => {
                setShowCandidatureDialog(false);
                setCvFile(null);
                setCandidatureMessage('');
              }}
              className="rounded-xl"
            >
              Annuler
            </Button>
            <Button
              onClick={submitCandidature}
              disabled={submittingCandidature}
              className="rounded-xl bg-emerald-600 hover:bg-emerald-700"
            >
              {submittingCandidature ? (
                <>
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                  Envoi en cours...
                </>
              ) : (
                <>
                  <Send className="w-4 h-4 mr-2" />
                  Envoyer ma candidature
                </>
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default BourseEmploi;
